import React from 'react';
import {
  WhiteInstantSearchBuilder,
  mappers as whiteInstantSearchMappers,
  AutocompleteHeader,
  AutocompleteItem,
  AutocompleteProductItem,
  AutocompleteHighlight,
  Loading,
  NoResults,
  Pagination,
} from '@white-nl/instantsearch-components';

import { Overview, Hit } from '../components/search/results/Hit';
import Menu from '../components/search/refinements/Menu';

const sectionMapping = {
  products: Craft.t('search', 'Products'),
  produkte: Craft.t('search', 'Products'),
  productos: Craft.t('search', 'Products'),
  produits: Craft.t('search', 'Products'),
  prodotti: Craft.t('search', 'Products'),
  producten: Craft.t('search', 'Products'),
  产品: Craft.t('search', 'Products'),
  services: Craft.t('search', 'Services'),
  dienstleistungen: Craft.t('search', 'Services'),
  servicios: Craft.t('search', 'Services'),
  service: Craft.t('search', 'Services'),
  servizi: Craft.t('search', 'Services'),
  diensten: Craft.t('search', 'Services'),
  服务: Craft.t('search', 'Services'),
  applications: Craft.t('search', 'Applications'),
  anwendungen: Craft.t('search', 'Applications'),
  aplicaciones: Craft.t('search', 'Applications'),
  applicazioni: Craft.t('search', 'Applications'),
  toepassingen: Craft.t('search', 'Applications'),
  应用: Craft.t('search', 'Applications'),
  downloads: Craft.t('search', 'Downloads'),
  descargas: Craft.t('search', 'Downloads'),
  téléchargements: Craft.t('search', 'Downloads'),
  download: Craft.t('search', 'Downloads'),
  下载: Craft.t('search', 'Downloads'),
  faq: Craft.t('search', 'FAQ'),
  'preguntas-más-frecuentes': Craft.t('search', 'FAQ'),
  常问问题: Craft.t('search', 'FAQ'),
  other: Craft.t('search', 'Other'),
  andere: Craft.t('search', 'Other'),
  otro: Craft.t('search', 'Other'),
  autre: Craft.t('search', 'Other'),
  altro: Craft.t('search', 'Other'),
  overig: Craft.t('search', 'Other'),
  其他: Craft.t('search', 'Other'),
};

const search = document.getElementById('section-search');
const settings = search?.dataset || null;
if (search && settings) {
  const whiteInstantSearchSearch = new WhiteInstantSearchBuilder();
  whiteInstantSearchSearch
    .withSearchkit(
      settings.indexName || '',
      settings.publicUrl || '',
      settings.jwt || '',
      settings.refreshUrl || '',
    )
    .withWhiteInstantSearchRouting({
      baseUrl: settings.baseUrl,
      addToPath: true,
      addTrailingSlashesToUrls: settings.trailingSlashes === '1',
      pathAttribute: 'section',
      writeDelay: 0,
    })
    .addAdditionalData({
      baseUrl: settings.baseUrl,
    })
    .addAutocompleteSearchingComponent([
      {
        settings: {
          placeholder: Craft.t('search', 'What are you looking for?'),
          sources: [
            {
              getQueryParams: (query, defaultFilters?) => ({
                query,
                filters: `type:01Products ${(defaultFilters ? `AND (${defaultFilters})` : '')}`,
              }),
              transformResponse: ({ hits }) => ((hits[0] || []).map((hit: any) => ({ ...hit }))),
              templates: {
                header: () => (<AutocompleteHeader title={Craft.t('search', 'Products')} />),
                item: ({
                  item,
                  components,
                  source,
                  state,
                }) => (
                  <AutocompleteProductItem
                    hit={item}
                    url={source.getItemUrl!({ item, state })!}
                    components={{ ...components, Highlight: AutocompleteHighlight }}
                    descriptionAttribute="description"
                    ratingAttribute="rating"
                    commentsAttribute="comments"
                    imageAttribute={['productImages', '0', 'transforms', 'xxs', 'url']}
                    imagePlaceholder={(
                      <div className="aa-ItemIcon aa-ItemIcon--picture aa-ItemIcon--alignTop">
                        <img src="/images/placeholder.png" alt={item.title} />
                      </div>
                    )}
                    priceAttribute="defaultPrice"
                    salePriceAttribute="defaultSalePrice"
                    onFillQuery={() => source.onFillQuery({ item, state })}
                  />
                ),
              },
            },
            {
              getQueryParams: (query, defaultFilters?) => ({
                query,
                filters: `NOT type:01Products ${(defaultFilters ? `AND (${defaultFilters})` : '')}`,
              }),
              templates: {
                item: ({
                  item,
                  components,
                  source,
                  state,
                }) => (
                  <AutocompleteItem
                    hit={item}
                    url={source.getItemUrl!({ item, state })!}
                    components={{ ...components, Highlight: AutocompleteHighlight }}
                    onFillQuery={() => source.onFillQuery({ item, state })}
                  />
                ),
              },
            },
          ],
        },
      },
    ])
    .addStatsComponent([
      {
        component: ({ stats }) => (
          stats.query ? Craft.t('search', 'Search results for "{query}"', { query: stats.query }) : Craft.t('search', 'All search results')
        ),
      },
    ])
    .addMenu([
      {
        settings: {
          attribute: 'section',
          classNames: {
            list: 'nav nav-underline',
            item: 'nav-item',
            selectedItem: 'active',
            link: 'nav-link',
            label: 'label',
            count: 'filters-count',
          },
          sortBy: (item1, item2) => {
            // custom order sorting
            const value1 = item1.escapedValue.toLowerCase();
            const value2 = item2.escapedValue.toLowerCase();
            const keys = Object.keys(sectionMapping);

            if (keys.includes(value1) && keys.includes(value2)) {
              if (keys.indexOf(value1) > keys.indexOf(value2)) {
                return 1;
              }
              if (keys.indexOf(value1) < keys.indexOf(value2)) {
                return -1;
              }
              return 0;
            }

            if (keys.includes(value1) && !keys.includes(value2)) {
              return -1;
            }
            if (!keys.includes(value1) && keys.includes(value2)) {
              return 1;
            }

            return 0;
          },
          transformItems: (items) => {
            const itemsWithData = items.map((item) => ({ ...item, data: null }));
            return whiteInstantSearchMappers.transformers.mapLabels(sectionMapping)(itemsWithData);
          },
          hardRefresh: search.classList.contains('section-search-index'),
        },
        component: Menu,
      },
    ]);

  if (search.classList.contains('section-search-index')) {
    whiteInstantSearchSearch
      .addStatsComponent([{ settings: {}, component: Overview }], '#white-instant-search-hits-custom')
      .setHitsPerPage(9999);
  } else {
    whiteInstantSearchSearch
      .setHitsPerPage(24)
      .addHitComponent(
        [
          {
            settings: {
              loading: {
                translations: {
                  loading: Craft.t('search', 'Loading search results...'),
                },
              },
              noResults: {
                translations: {
                  noResults: Craft.t('search', 'Sorry, we can\'t find any results!'),
                  refinements: Craft.t('search', 'Please try another query.'),
                  query: Craft.t('search', 'Please try another query.'),
                },
              },
            },
            component: Hit,
            loadingComponent: Loading,
            noResultsComponent: NoResults,
          },
        ],
      )
      .setScrolling({
        includeAttributes: ['page'],
      })
      .addPaginationComponent([
        {
          settings: {
            showFirst: false,
            showLast: false,
            classNames: {
              list: 'pagination',
              item: 'page-item',
              previousPageItem: 'previous',
              selectedItem: 'active',
              disabledItem: 'disabled',
              nextPageItem: 'next',
              link: 'page-link',
            },
            translations: {
              previousPageItemText: Craft.t('pagination', 'Previous'),
              previousPageItemAriaLabel: Craft.t('pagination', 'Go to previous page'),
              nextPageItemText: Craft.t('pagination', 'Next'),
              nextPageItemAriaLabel: Craft.t('pagination', 'Go to next page'),
            },
          },
          component: Pagination,
        },
      ]);
  }
  whiteInstantSearchSearch
    .where(`siteId:${settings.siteId} AND ((NOT type:01Products) OR isAvailableInSiteRegion:true)`)
    .build()
    .show();
}
